(() => {
  const initRoyalProduct = (root) => {
    if (!root || root.dataset.initialized === 'true') return;
    root.dataset.initialized = 'true';

    const price = root.querySelector('[data-price]');
    const comparePrice = root.querySelector('[data-compare-price]');
    const addButton = root.querySelector('[data-add-button]');
    const addButtonText = root.querySelector('[data-add-button-text]');
    const mobilePrice = root.querySelector('[data-mobile-price]');
    const stickyButton = root.querySelector('[data-sticky-add]');
    const mobileCart = root.querySelector('[data-mobile-cart]');
    const productForm = root.querySelector('.tk-product-form');
    const selectedVariantInput = root.querySelector('[data-selected-variant]');
    const variantDataElement = root.querySelector('[data-variant-data]');
    const optionInputs = Array.from(root.querySelectorAll('[data-option-input]'));
    const optionGroups = Array.from(root.querySelectorAll('[data-option-group]'));

    let variants = [];
    if (variantDataElement) {
      try {
        variants = JSON.parse(variantDataElement.textContent || '[]');
      } catch (error) {
        console.error('THCA King: unable to parse product variant data.', error);
      }
    }

    const selectMedia = (mediaId) => {
      if (!mediaId) return;

      root.querySelectorAll('.tk-gallery__item').forEach((item) => {
        const active = String(item.dataset.mediaId) === String(mediaId);
        item.hidden = !active;
        item.classList.toggle('is-active', active);
      });

      root.querySelectorAll('[data-gallery-thumb]').forEach((thumb) => {
        const active = String(thumb.dataset.galleryThumb) === String(mediaId);
        thumb.classList.toggle('is-active', active);
        thumb.setAttribute('aria-pressed', active ? 'true' : 'false');
      });
    };

    root.querySelectorAll('[data-gallery-thumb]').forEach((thumb) => {
      thumb.addEventListener('click', () => selectMedia(thumb.dataset.galleryThumb));
    });

    const getSelectedOptions = () => {
      const selected = [];

      optionGroups.forEach((group) => {
        const position = Number(group.dataset.optionPosition);
        const checked = group.querySelector('[data-option-input]:checked');
        if (checked) selected[position] = checked.value;
      });

      return selected;
    };

    const optionsMatch = (variant, selectedOptions) => (
      variant.options.every((value, index) => value === selectedOptions[index])
    );

    const variantMatchesPrefix = (variant, selectedOptions, endIndex) => {
      for (let index = 0; index <= endIndex; index += 1) {
        if (variant.options[index] !== selectedOptions[index]) return false;
      }
      return true;
    };

    const setSelectedOption = (position, value) => {
      const input = optionInputs.find((item) => (
        Number(item.dataset.optionPosition) === position && item.value === value
      ));

      if (!input) return;
      input.checked = true;

      const group = input.closest('[data-option-group]');
      group?.querySelectorAll('[data-variant-card]').forEach((card) => {
        const active = card.dataset.optionValue === value;
        card.classList.toggle('is-selected', active);
      });
    };

    const resolveSelections = (changedPosition) => {
      const selectedOptions = getSelectedOptions();

      for (let position = changedPosition + 1; position < optionGroups.length; position += 1) {
        const currentValue = selectedOptions[position];
        const currentStillAvailable = variants.some((variant) => (
          variant.available
          && variant.options[position] === currentValue
          && variantMatchesPrefix(variant, selectedOptions, position)
        ));

        if (currentStillAvailable) continue;

        const fallbackVariant = variants.find((variant) => (
          variant.available
          && variantMatchesPrefix(variant, selectedOptions, position - 1)
        ));

        if (fallbackVariant) {
          selectedOptions[position] = fallbackVariant.options[position];
          setSelectedOption(position, fallbackVariant.options[position]);
        }
      }

      return getSelectedOptions();
    };

    const parseGrams = (rawValue) => {
      if (!rawValue) return 0;
      const value = String(rawValue).toLowerCase().replace(/,/g, '').trim();

      const explicitGrams = value.match(/(\d+(?:\.\d+)?)\s*(?:grams?|g)\b/);
      if (explicitGrams) return Number.parseFloat(explicitGrams[1]);

      if (/\b1\s*\/\s*4\s*lb\b|\b1\/4lb\b|quarter\s+pound/.test(value)) return 113.4;
      if (/\b1\s*\/\s*2\s*lb\b|\b1\/2lb\b|half\s+pound/.test(value)) return 225;
      if (/\b1\s*lb\b|\b1lb\b|\bone\s+pound\b/.test(value)) return 450;

      const ounces = value.match(/(\d+(?:\.\d+)?)\s*(?:ounces?|oz)\b/);
      if (ounces) return Number.parseFloat(ounces[1]) * 28;

      return 0;
    };

    const formatUnitPrice = (priceCents, grams) => {
      if (!priceCents || !grams) return '';
      const currency = window.Shopify?.currency?.active || 'USD';
      const formatter = new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
      return `${formatter.format((priceCents / 100) / grams)}/gram`;
    };

    const getRepresentativeVariant = (position, value, selectedOptions) => {
      const exactOptions = [...selectedOptions];
      exactOptions[position] = value;

      return variants.find((variant) => optionsMatch(variant, exactOptions))
        || variants.find((variant) => {
          if (variant.options[position] !== value) return false;
          for (let index = 0; index < position; index += 1) {
            if (variant.options[index] !== selectedOptions[index]) return false;
          }
          return variant.available;
        })
        || variants.find((variant) => variant.options[position] === value)
        || null;
    };

    const updateOptionCards = (selectedOptions) => {
      optionGroups.forEach((group) => {
        const position = Number(group.dataset.optionPosition);

        group.querySelectorAll('[data-variant-card]').forEach((card) => {
          const value = card.dataset.optionValue;
          const input = card.querySelector('[data-option-input]');
          const priceLabel = card.querySelector('[data-option-price]');
          const unitLabel = card.querySelector('[data-option-unit]');
          const statusLabel = card.querySelector('[data-option-status]');

          const available = variants.some((variant) => {
            if (!variant.available || variant.options[position] !== value) return false;
            for (let index = 0; index < position; index += 1) {
              if (variant.options[index] !== selectedOptions[index]) return false;
            }
            return true;
          });

          card.classList.toggle('is-sold-out', !available);
          if (input) input.disabled = !available;

          const representativeVariant = getRepresentativeVariant(position, value, selectedOptions);
          const grams = parseGrams(value);

          if (priceLabel) {
            if (grams > 0 && representativeVariant) {
              priceLabel.textContent = representativeVariant.priceFormatted || '';
              priceLabel.hidden = false;
            } else {
              priceLabel.textContent = '';
              priceLabel.hidden = true;
            }
          }

          if (unitLabel) {
            const unitPrice = representativeVariant
              ? formatUnitPrice(representativeVariant.priceCents, grams)
              : '';
            unitLabel.textContent = unitPrice;
            unitLabel.hidden = !unitPrice;
          }

          if (statusLabel) {
            statusLabel.textContent = available ? '' : 'Sold out';
            statusLabel.hidden = available;
          }
        });
      });
    };

    const updateVariant = (variant) => {
      if (!variant) {
        if (addButton) addButton.disabled = true;
        if (stickyButton) stickyButton.disabled = true;
        if (addButtonText) addButtonText.textContent = 'UNAVAILABLE';
        if (stickyButton) stickyButton.textContent = 'Unavailable';
        return;
      }

      if (selectedVariantInput) selectedVariantInput.value = variant.id;
      if (price) price.textContent = variant.priceFormatted || '';
      if (mobilePrice) mobilePrice.textContent = variant.priceFormatted || '';

      if (comparePrice) {
        if (variant.comparePriceFormatted) {
          comparePrice.textContent = variant.comparePriceFormatted;
          comparePrice.classList.remove('is-hidden');
        } else {
          comparePrice.textContent = '';
          comparePrice.classList.add('is-hidden');
        }
      }

      const available = variant.available === true;
      if (addButton) addButton.disabled = !available;
      if (stickyButton) stickyButton.disabled = !available;
      if (addButtonText) addButtonText.textContent = available ? 'ADD TO THE ROYAL STASH' : 'SOLD OUT';
      if (stickyButton) stickyButton.textContent = available ? 'Add to Stash' : 'Sold Out';

      selectMedia(variant.mediaId);
      if (variant.url && window.history?.replaceState) {
        window.history.replaceState({}, '', variant.url);
      }
    };

    const refreshSelection = (changedPosition = -1) => {
      const selectedOptions = changedPosition >= 0
        ? resolveSelections(changedPosition)
        : getSelectedOptions();

      const variant = variants.find((item) => optionsMatch(item, selectedOptions)) || null;
      updateOptionCards(selectedOptions);
      updateVariant(variant);
    };

    optionInputs.forEach((input) => {
      input.addEventListener('change', () => {
        const position = Number(input.dataset.optionPosition);
        setSelectedOption(position, input.value);
        refreshSelection(position);
      });
    });

    if (variants.length && optionGroups.length) refreshSelection();

    if (stickyButton && productForm) {
      stickyButton.addEventListener('click', () => {
        if (typeof productForm.requestSubmit === 'function') productForm.requestSubmit();
        else addButton?.click();
      });
    }

    if (mobileCart && addButton && 'IntersectionObserver' in window) {
      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          const show = !entry.isIntersecting && window.innerWidth < 750;
          mobileCart.hidden = !show;
          mobileCart.classList.toggle('is-visible', show);
        });
      }, { threshold: 0.05 });
      observer.observe(addButton);
    }
  };

  const boot = () => document.querySelectorAll('[data-royal-product]').forEach(initRoyalProduct);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();

  document.addEventListener('shopify:section:load', (event) => {
    event.target.querySelectorAll?.('[data-royal-product]').forEach(initRoyalProduct);
  });
})();
